// The parts library. Every shape is path data in unit space: x and y in -1..1, y up like the sheet.
// Only M L Q C Z, absolute. Hand-feel is deliberate: the wobble and the lopsidedness are the style.

type P = [number, number];

export interface PartDef { group: string; label: string; d: string }

export const GROUPS = ['eyes', 'noses', 'mouths', 'brows', 'extras'] as const;

function rng(seed: number) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const f = (n: number) => String(Math.round(n * 100) / 100);
const poly = (pts: P[]) => 'M' + pts.map(p => `${f(p[0])} ${f(p[1])}`).join('L') + 'Z';
const polys = (...all: P[][]) => all.map(poly).join('');

function blobPts(cx: number, cy: number, rx: number, ry: number, seed: number, wob = 0.07, n = 24): P[] {
  const r = rng(seed), out: P[] = [];
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2;
    const k = 1 + (r() - 0.5) * 2 * wob;
    out.push([cx + Math.cos(a) * rx * k, cy + Math.sin(a) * ry * k]);
  }
  return out;
}

function starPts(ro: number, ri: number, n: number, seed: number, rot = Math.PI / 2): P[] {
  const r = rng(seed), out: P[] = [];
  for (let i = 0; i < n * 2; i++) {
    const a = rot + (i / (n * 2)) * Math.PI * 2 + (r() - 0.5) * 0.08;
    const rad = (i % 2 ? ri : ro) * (1 + (r() - 0.5) * 0.12);
    out.push([Math.cos(a) * rad, Math.sin(a) * rad]);
  }
  return out;
}

function ribbon(pts: P[], w: number | ((t: number) => number)): P[] {
  const left: P[] = [], right: P[] = [];
  pts.forEach((p, i) => {
    const a = pts[Math.max(0, i - 1)], b = pts[Math.min(pts.length - 1, i + 1)];
    let tx = b[0] - a[0], ty = b[1] - a[1];
    const l = Math.hypot(tx, ty) || 1;
    tx /= l; ty /= l;
    const half = (typeof w === 'number' ? w : w(i / (pts.length - 1))) / 2;
    left.push([p[0] - ty * half, p[1] + tx * half]);
    right.push([p[0] + ty * half, p[1] - tx * half]);
  });
  return left.concat(right.reverse());
}

function curveBand(yc: (x: number) => number, x0: number, x1: number, thick: (t: number) => number, n = 18, seed = 1): P[] {
  const r = rng(seed), pts: P[] = [];
  for (let i = 0; i <= n; i++) {
    const t = i / n, x = x0 + (x1 - x0) * t;
    pts.push([x, yc(x) + (r() - 0.5) * 0.02]);
  }
  return ribbon(pts, thick);
}

function grin(): string {
  const r = rng(11), top: P[] = [], bot: P[] = [];
  const yt = (x: number) => 0.55 - 0.55 * (1 - x * x);
  const h = (x: number) => 0.08 + 0.62 * Math.pow(1 - x * x, 0.6);
  const d = (x: number) => 0.22 * Math.sqrt(Math.max(0, 1 - x * x));
  const n = 8, j = () => (r() - 0.5) * 0.06;
  for (let k = 0; k <= n; k++) {
    const x = -1 + (2 * k) / n;
    top.push([x + j(), yt(x) - (k % 2 ? d(x) : 0) + j()]);
  }
  for (let k = n; k >= 0; k--) {
    const x = -1 + (2 * k) / n;
    bot.push([x + j(), yt(x) - h(x) + (k % 2 === 0 && k > 0 && k < n ? d(x) : 0) + j()]);
  }
  return poly(top.concat(bot));
}

function toothy(): string {
  const r = rng(23), n = 7, w = 2 / n, j = () => (r() - 0.5) * 0.05;
  const top: P[] = [], bot: P[] = [];
  for (let k = 0; k < n; k++) {
    const y = k % 2 === 0 ? 0.14 : 0.5;
    top.push([-1 + k * w + j(), y + j()], [-1 + (k + 1) * w + j(), y + j()]);
  }
  for (let k = n - 1; k >= 0; k--) {
    const y = k % 2 === 1 ? -0.14 : -0.5;
    bot.push([-1 + (k + 1) * w + j(), y + j()], [-1 + k * w + j(), y + j()]);
  }
  return poly(top.concat(bot));
}

function stitched(): string {
  const r = rng(5), out: P[][] = [];
  const bars = [-0.9, -0.54, -0.18, 0.18, 0.54, 0.9];
  bars.forEach((x, i) => {
    const s = (r() - 0.5) * 0.08;
    out.push([[x - 0.045 + s, 0.3], [x + 0.045 + s, 0.3], [x + 0.045 - s, -0.3], [x - 0.045 - s, -0.3]]);
    if (i < bars.length - 1) {
      const a = x + 0.075, b = bars[i + 1] - 0.075, y = (r() - 0.5) * 0.08;
      out.push([[a, y + 0.05], [b, y + 0.06], [b, y - 0.05], [a, y - 0.04]]);
    }
  });
  return polys(...out);
}

function smirk(): string {
  const band = curveBand(x => -0.3 + 0.28 * x + 0.22 * x * x, -1, 0.78, t => 0.05 + 0.17 * Math.pow(Math.sin(Math.PI * t), 0.7), 16, 3);
  const tick = ribbon([[0.82, 0.12], [0.92, 0.3], [0.99, 0.42]], 0.07);
  return polys(band, tick);
}

function spider(): string {
  const body = blobPts(0, -0.08, 0.3, 0.34, 4, 0.05);
  const head = blobPts(0, 0.28, 0.17, 0.15, 5, 0.05, 12);
  const legs: P[][] = [];
  const side = (sx: number) => {
    const set: P[][] = [
      [[0.18, 0.2], [0.55, 0.62], [0.9, 0.32]],
      [[0.26, 0.06], [0.7, 0.3], [0.97, -0.02]],
      [[0.26, -0.08], [0.68, -0.2], [0.92, -0.5]],
      [[0.2, -0.22], [0.5, -0.55], [0.72, -0.92]],
    ];
    set.forEach((l, i) => legs.push(ribbon(l.map(p => [p[0] * sx * (1 + i * 0.01), p[1]] as P), 0.07)));
  };
  side(1); side(-0.94);
  return polys(body, head, ...legs);
}

function cat(): string {
  const head = blobPts(0, 0.45, 0.3, 0.27, 6, 0.04, 14);
  const earL: P[] = [[-0.29, 0.52], [-0.31, 0.97], [-0.07, 0.7]];
  const earR: P[] = [[0.07, 0.7], [0.3, 0.94], [0.28, 0.52]];
  const body = 'M-0.35 0.3C-0.6 -0.2 -0.55 -0.7 -0.5 -0.9L0.45 -0.92C0.5 -0.6 0.45 -0.1 0.3 0.3Z';
  const tail = ribbon([[0.44, -0.85], [0.85, -0.82], [0.97, -0.42], [0.8, -0.05]], 0.12);
  return poly(head) + poly(earL) + poly(earR) + body + poly(tail);
}

function ghost(): string {
  const outer = 'M-0.7 -0.9L-0.72 0.1C-0.75 0.9 0.7 0.95 0.72 0.1L0.75 -0.92Q0.55 -0.7 0.4 -0.9Q0.2 -0.7 0 -0.92Q-0.2 -0.72 -0.38 -0.9Q-0.55 -0.7 -0.7 -0.9Z';
  return outer + poly(blobPts(-0.27, 0.22, 0.12, 0.17, 7)) + poly(blobPts(0.26, 0.2, 0.13, 0.16, 8)) + poly(blobPts(0.0, -0.3, 0.14, 0.22, 9));
}

function skull(): string {
  const outer = 'M-0.65 0.1C-0.7 0.95 0.68 0.98 0.66 0.05L0.5 -0.25L0.45 -0.8L-0.42 -0.82L-0.48 -0.28Z';
  const slit = (x: number): P[] => [[x - 0.03, -0.5], [x + 0.03, -0.5], [x + 0.03, -0.8], [x - 0.03, -0.8]];
  return outer + poly(blobPts(-0.28, 0.1, 0.17, 0.2, 12)) + poly(blobPts(0.28, 0.08, 0.17, 0.2, 13))
    + poly([[-0.08, -0.2], [0.07, -0.2], [0.0, -0.02]]) + poly(slit(-0.2)) + poly(slit(0.0)) + poly(slit(0.2));
}

function swirl(): string {
  const pts: P[] = [];
  for (let i = 0; i <= 48; i++) {
    const t = i / 48, a = t * 3.2 * Math.PI * 2 + 0.4, r = 0.12 + 0.8 * t;
    pts.push([Math.cos(a) * r, Math.sin(a) * r]);
  }
  return poly(ribbon(pts, t => 0.07 + 0.05 * t));
}

function bat(): string {
  return 'M0 0.38L0.1 0.72L0.2 0.4Q0.55 0.62 1 0.5Q0.88 0.25 0.9 -0.05Q0.72 0.08 0.62 -0.15Q0.52 0 0.36 -0.1Q0.28 -0.3 0.1 -0.42L0.02 -0.55'
    + 'L-0.06 -0.42Q-0.28 -0.3 -0.38 -0.12Q-0.52 -0.02 -0.6 -0.17Q-0.74 0.06 -0.9 -0.02Q-0.86 0.25 -0.98 0.48Q-0.55 0.6 -0.2 0.36L-0.12 0.7Z';
}

const def = (group: string, label: string, d: string): PartDef => ({ group, label, d });

export const PARTS: Record<string, PartDef> = {
  'eye-triangle': def('eyes', 'Triangle', 'M-0.95 -0.7L0.02 0.94L0.96 -0.66Q0 -0.8 -0.95 -0.7Z'),
  'eye-crescent': def('eyes', 'Happy', 'M-0.95 -0.1C-0.7 0.8 0.6 0.9 0.95 -0.2C0.5 0.35 -0.5 0.4 -0.95 -0.1Z'),
  'eye-round': def('eyes', 'Round', poly(blobPts(0, 0, 0.9, 0.95, 1))),
  'eye-angry': def('eyes', 'Angry', 'M-0.95 0.62L0.96 -0.28Q0.2 -0.95 -0.9 -0.42Q-1 0.1 -0.95 0.62Z'),
  'eye-sad': def('eyes', 'Droopy', 'M-0.97 -0.55Q-0.2 0.3 0.95 0.65Q0.5 -0.35 -0.2 -0.7Q-0.7 -0.8 -0.97 -0.55Z'),
  'eye-cat': def('eyes', 'Cat slit', 'M0 1C0.5 0.4 0.45 -0.5 0.02 -1C-0.4 -0.5 -0.48 0.4 0 1Z'),
  'eye-star': def('eyes', 'Sparkle', poly(starPts(1, 0.28, 4, 2, Math.PI / 2 + 0.06))),
  'eye-x': def('eyes', 'X', 'M-0.85 0.95L0.02 0.18L0.88 0.97L0.98 0.8L0.2 0L0.95 -0.82L0.82 -0.96L0 -0.2L-0.9 -0.95L-0.98 -0.78L-0.18 0L-0.95 0.82Z'),
  'eye-wink': def('eyes', 'Wink', 'M-0.95 0.5Q0 -0.1 0.95 0.45L0.9 0.1Q0 -0.55 -0.9 0.05Z'),

  'nose-triangle': def('noses', 'Triangle', 'M-0.85 -0.7L-0.05 0.8L0.9 -0.78Z'),
  'nose-heart': def('noses', 'Heart', 'M0.03 0.95C0.5 0.4 1 -0.1 0.85 -0.55C0.7 -0.95 0.1 -0.85 0 -0.45C-0.12 -0.9 -0.85 -0.9 -0.92 -0.5C-0.98 -0.05 -0.45 0.4 0.03 0.95Z'),
  'nose-slits': def('noses', 'Two slits', 'M-0.7 0.3L-0.25 -0.6L-0.05 -0.5L-0.5 0.4ZM0.1 -0.55L0.5 0.35L0.72 0.28L0.3 -0.65Z'),
  'nose-button': def('noses', 'Button', poly(blobPts(0, 0, 0.7, 0.6, 14))),

  'mouth-grin': def('mouths', 'Zigzag grin', grin()),
  'mouth-toothy': def('mouths', 'Toothy', toothy()),
  'mouth-fangs': def('mouths', 'Fangs', 'M-1 0.4L-0.7 0.2L-0.5 0.1L-0.42 -0.42L-0.28 0.05L0 -0.02L0.34 0L0.46 -0.45L0.6 0.1L0.8 0.2L1 0.45L0.75 -0.35L0.4 -0.65L0 -0.75L-0.4 -0.68L-0.75 -0.4Z'),
  'mouth-scream': def('mouths', 'Scream', poly(blobPts(0, 0, 0.62, 0.95, 15, 0.06, 18))),
  'mouth-frown': def('mouths', 'Frown', poly(curveBand(x => -0.35 + 0.55 * (1 - x * x), -1, 1, t => 0.14 + 0.12 * Math.sin(Math.PI * t) + 0.03 * Math.sin(t * 17), 16, 9))),
  'mouth-stitched': def('mouths', 'Stitched', stitched()),
  'mouth-smirk': def('mouths', 'Smirk', smirk()),
  'mouth-tooth': def('mouths', 'One tooth', 'M-0.8 0.9L0.6 0.95L0.85 0.7L0.8 -0.85L-0.75 -0.9L-0.85 0.1Z'),

  'brow-angry': def('brows', 'Angry', 'M-0.95 0.55L0.98 -0.25L0.92 -0.62L-0.9 0.1Z'),
  'brow-worried': def('brows', 'Worried', 'M-0.96 -0.62Q-0.1 -0.25 0.96 0.5L0.85 0.85Q-0.2 0.2 -0.88 -0.3Z'),

  'bat': def('extras', 'Bat', bat()),
  'moon': def('extras', 'Moon', 'M0.35 0.9C-0.9 0.7 -0.95 -0.7 0.4 -0.92C-0.45 -0.55 -0.5 0.5 0.35 0.9Z'),
  'star': def('extras', 'Star', poly(starPts(0.98, 0.42, 5, 3))),
  'spider': def('extras', 'Spider', spider()),
  'cat': def('extras', 'Cat', cat()),
  // a cat's head with its ears up and the fur on its cheeks on end, for scraping under a face
  'cat-head': def('extras', 'Cat head', 'M0 -0.92C0.45 -0.92 0.8 -0.72 0.88 -0.44L1 -0.34L0.9 -0.24L1 -0.1L0.9 0C0.95 0.3 0.92 0.5 0.86 0.98L0.42 0.62C0.2 0.68 -0.2 0.68 -0.42 0.62L-0.86 0.98C-0.92 0.5 -0.95 0.3 -0.9 0L-1 -0.1L-0.9 -0.24L-1 -0.34L-0.88 -0.44C-0.8 -0.72 -0.45 -0.92 0 -0.92Z'),
  'ghost': def('extras', 'Ghost', ghost()),
  'skull': def('extras', 'Skull', skull()),
  'flame': def('extras', 'Flame', 'M0.02 0.98C0.5 0.4 0.75 -0.1 0.55 -0.55C0.45 -0.9 -0.4 -0.95 -0.55 -0.5C-0.7 -0.05 -0.3 0.1 -0.15 0.45C-0.05 0.3 -0.05 0.6 0.02 0.98Z'),
  'swirl': def('extras', 'Swirl', swirl()),
};
